/**
 * THE ANNUITY CHOICE, AS THREE QUESTIONS ANSWERED TOGETHER.
 *
 * Owner's standing challenge, 24 Sep 2026: the app is sold direct to consumers and gives personal
 * advice, so being technically right is only half the job — the client has to be able to follow it.
 *
 * The annuity screen used to ask one question, "how much do you want back?", on a slider whose label
 * ("Leave some money to your family at the end?") read as "at the end of your life". For a client in
 * residential care, whose actual worry is living a long time, that label appeared to answer the worry
 * and answered the opposite: the money comes back in full only if death comes SOON. Meanwhile the
 * third question a client always asks — what if I need it back? — was answered nowhere, and two
 * screens answered it wrongly by saying the money was locked away for good.
 *
 * So the choice is now three cards, each answering all three questions at once:
 *
 *      What do I get?            What does my family get?        What if I need it back?
 *
 * Two of the three are shapes we price from the published rate sheet and model end to end. The
 * third, the full guarantee, is shown but never modelled on a guess — see below.
 *
 * WHY THE THIRD CARD CANNOT BE MODELLED WITHOUT A QUOTE. No single annuity pays the whole amount to
 * the estate whenever death comes: the capital access schedule is what buys the 60% assets-test
 * treatment, and a product paying 100% for life would be counted in full. Providers build this shape
 * as TWO things bought together — a schedule-compliant annuity and a life policy that tops up
 * whatever the annuity would not return. The income can be read off the published table, because the
 * pair is priced as one product. The MEANS TEST cannot: it depends on how the premium splits between
 * the two parts, which only the provider prices, and the insurance half is counted at the greater of
 * the premium paid and its surrender value. So this card shows the income, states the guarantee, and
 * asks for a quote rather than inventing a split. It is never ranked against the other two.
 */
import React from 'react';
import { formatCurrency } from '../../utils/index.ts';
import {
    lifetimeAnnuityRate,
    guaranteedAnnuityRate,
} from '../../../../shared/src/calculation/annuityStrategies.ts';
import { capitalAccessSchedule, withdrawalWords, scheduleYearLabel } from './annuityWords.ts';

export type AnnuityShape = 'highestIncome' | 'moneyBack' | 'guaranteed';

/*
 * `guaranteedShapeAvailable` moved to shared code (carePlusSplit.ts) on 27 Sep 2026, because the
 * suggestion engines run on the server and must give the same answer this screen gives. It is
 * re-exported here so the existing imports keep working.
 */
export { guaranteedShapeAvailable } from '../../../../shared/src/calculation/carePlusSplit.ts';

interface Dimension {
    label: string;
    value: string;
    tone: 'income' | 'estate' | 'access' | 'none';
}

interface ShapeCard {
    key: AnnuityShape;
    title: string;
    dimensions: [Dimension, Dimension, Dimension];
    selectable: boolean;
    note?: string;
}

const toneClass: Record<Dimension['tone'], string> = {
    income: 'text-blue-700',
    estate: 'text-violet-700',
    access: 'text-slate-800',
    none: 'text-rose-700',
};

export interface AnnuityShapeChoiceProps {
    purchaseAmount: number;
    /** The owner's age at purchase; the sheet prices on it. */
    age: number | null;
    sex?: unknown;
    partnerAge?: number | null;
    /** Joint ownership or a reversionary nomination: the income runs on two lives. */
    joint?: boolean;
    indexation?: boolean;
    /** The capital access period for these lives, from `capitalAccessPeriodFor`. */
    periodYears: number | null;
    /** 0-100. Anything above 0 selects the money-back card. */
    residualPercent: number;
    onChange: (residualPercent: number) => void;
    /** Whose annuity it is, for the copy. "you" by default. */
    ownerName?: string | null;
    /**
     * Set to "Joe and Kim" where the income runs on two lives. The lump sum is then payable only
     * after BOTH have died (Liquid Lifetime PDS: "only payable after the death of all persons
     * included as part of the investment"), so the card must not name one of them alone.
     */
    bothLives?: string | null;
    /** Pass false to hide the full-guarantee card; see `guaranteedShapeAvailable`. */
    showGuaranteed?: boolean;
    /** True when the client has picked the full-guarantee shape, which is quote-driven. */
    wantsGuarantee?: boolean;
    onGuaranteeChange?: (wants: boolean) => void;
    /** Opens the quote panel when the client picks the guaranteed shape. */
    onWantQuote?: () => void;
}

const AnnuityShapeChoice: React.FC<AnnuityShapeChoiceProps> = ({
    purchaseAmount,
    age,
    sex,
    partnerAge = null,
    joint = false,
    indexation = false,
    periodYears,
    residualPercent,
    onChange,
    ownerName,
    bothLives = null,
    showGuaranteed = false,
    wantsGuarantee = false,
    onGuaranteeChange,
    onWantQuote,
}) => {
    if (!(purchaseAmount > 0) || age == null || !(age > 0)) return null;

    const priceAt = (residual: number) =>
        lifetimeAnnuityRate({ age, sex: sex as never, indexation, residualCapital: residual, partnerAge: joint ? partnerAge : null });

    const nil = priceAt(0);
    const full = priceAt(1);
    const guaranteed = guaranteedAnnuityRate({ age, sex: sex as never });

    const halfLabel = periodYears ? (Number.isInteger(periodYears / 2) ? String(periodYears / 2) : `${Math.floor(periodYears / 2)}½`) : null;
    const owner = (ownerName || 'you').trim() || 'you';
    const dies = owner === 'you' ? 'you die' : `${owner} dies`;

    /*
     * THE COLUMN HEADINGS CARRY THE WHOLE DISTINCTION.
     *
     * Owner, 26 Sep 2026, having picked "pay the highest income now" and then seen "the full amount
     * guaranteed to your family, whenever you die" still on offer: "it reads a bit contradictory."
     * It is not contradictory — the income stopping and the capital returning are different things,
     * and the shape that stops the income while guaranteeing the capital is exactly what the market
     * sells for aged care. But nothing on the screen said so. "Income" and "If Joe dies" sat in the
     * same row as though they were alternatives.
     *
     * So the headings now say which is which: the income is what arrives WHILE THE OWNER IS ALIVE,
     * and the death column is a LUMP SUM, not a continuation of payments.
     */
    const incomeLabel = 'Income while alive';
    const deathLabel = bothLives
        ? `Lump sum once ${bothLives} have both died`
        : owner === 'you' ? 'Lump sum when you die' : `Lump sum when ${owner} dies`;

    const cards: ShapeCard[] = [
        {
            key: 'highestIncome',
            title: 'The highest income',
            selectable: true,
            dimensions: [
                { label: incomeLabel, value: `${formatCurrency(purchaseAmount * nil.rate)}/yr`, tone: 'income' },
                { label: deathLabel, value: 'nothing', tone: 'none' },
                { label: 'If you need it back', value: 'nothing', tone: 'none' },
            ],
        },
        {
            key: 'moneyBack',
            title: 'Money back if things change, or if you die early',
            selectable: true,
            dimensions: [
                { label: incomeLabel, value: `${formatCurrency(purchaseAmount * full.rate)}/yr`, tone: 'income' },
                {
                    label: deathLabel,
                    /*
                     * "for 3½ yrs" read as "this much a year, for three and a half years", which is
                     * the opposite of what it means. It is one payment, made only if death falls
                     * inside that window. Owner-caught on the live screen, 26 Sep 2026.
                     */
                    value: halfLabel ? `${formatCurrency(purchaseAmount)} if within ${halfLabel} yrs` : formatCurrency(purchaseAmount),
                    tone: 'estate',
                },
                /*
                 * "up to $100,000" was true only TODAY (owner-caught, 27 Sep 2026). The cash-out cap
                 * falls in a straight line to nothing over the schedule period, which the table
                 * underneath showed while the card implied a standing figure. A card that has to be
                 * corrected by the table below it is worse than no card.
                 */
                {
                    label: 'If you need it back',
                    value: periodYears
                        ? `${formatCurrency(purchaseAmount)} now, nil by ${periodYears} yrs`
                        : `${formatCurrency(purchaseAmount)} now, falling each year`,
                    tone: 'access',
                },
            ],
        },
    ];

    if (showGuaranteed) {
        /*
         * OWNER ON THE LIVE SCREEN, 25 Sep 2026: "when I tried to change 'what matters most to you',
         * it won't let me select 'the full amount guaranteed'." It was deliberately inert, because
         * we cannot model its means test without a quote — but an inert card sitting in a row of
         * selectable ones just reads as broken. It is selectable now: choosing it opens the quote
         * panel and says plainly that the plan cannot be compared until the quote is in. Selecting
         * it does not change the modelled annuity, so nothing is ever ranked on invented numbers.
         */
        cards.push({
            key: 'guaranteed',
            title: 'The full amount guaranteed to your family, whenever you die',
            selectable: true,
            note: 'An annuity and a life policy bought together. Only the provider prices the split, so choosing this asks you for their quote.',
            dimensions: [
                {
                    label: incomeLabel,
                    value: guaranteed.extrapolated ? 'needs a quote' : `about ${formatCurrency(purchaseAmount * guaranteed.rate)}/yr`,
                    tone: 'income',
                },
                { label: deathLabel, value: `${formatCurrency(purchaseAmount)} always`, tone: 'estate' },
                { label: 'If you need it back', value: 'some — your quote states it', tone: 'access' },
            ],
        });
    }

    const selected: AnnuityShape = wantsGuarantee ? 'guaranteed' : residualPercent <= 0 ? 'highestIncome' : 'moneyBack';
    const schedule = capitalAccessSchedule({ purchaseAmount, residualPct: residualPercent, periodYears });

    return (
        <div className="space-y-2">
            <div className="space-y-2">
                {cards.map(card => {
                                    const isSelected = selected === card.key;
                    return (
                        <button
                            key={card.key}
                            type="button"
                            aria-pressed={card.selectable ? isSelected : undefined}
                            onClick={() => {
                                if (card.key === 'highestIncome') { onGuaranteeChange?.(false); onChange(0); }
                                else if (card.key === 'moneyBack') { onGuaranteeChange?.(false); onChange(100); }
                                else { onGuaranteeChange?.(true); onWantQuote?.(); }
                            }}
                            className={`w-full text-left rounded-lg px-3 py-2.5 transition-colors ${
                                isSelected
                                    ? 'border-2 border-blue-600 bg-white'
                                    : 'border border-slate-200 bg-white hover:bg-slate-50'
                            }`}
                        >
                            <p className={`text-[12.5px] font-bold mb-1.5 ${isSelected ? 'text-blue-900' : 'text-slate-900'}`}>{card.title}</p>
                            <div className="grid grid-cols-3 gap-2">
                                {card.dimensions.map(d => (
                                    <div key={d.label}>
                                        <p className="text-[9.5px] uppercase tracking-wide font-bold text-slate-500">{d.label}</p>
                                        <p className={`text-[13px] font-bold ${toneClass[d.tone]}`}>{d.value}</p>
                                    </div>
                                ))}
                            </div>
                            {card.note && <p className="mt-1.5 text-[10.5px] leading-relaxed text-slate-500">{card.note}</p>}
                        </button>
                    );
                })}
            </div>

            {/*
              * WHO ACTUALLY RECEIVES THE LUMP SUM. The heading says "your family", which is what a
              * client recognises, but the contract pays whoever is nominated as beneficiary and
              * otherwise the estate — and for super money the nomination has its own rules. Saying
              * "family" alone would be the kind of near-enough that matters when the tool is giving
              * personal advice, so the precise version sits here, once, under the cards.
              */}
            <p className="text-[10.5px] leading-relaxed text-slate-500">
                The lump sum goes to whoever is nominated as beneficiary, or to the estate if nobody is.
                Income is {indexation ? 'the first year’s payment, rising with prices after that' : 'the same every year'}
                , from {nil.extrapolated ? 'an estimate based on the published rates, which stop at age 75' : 'the published rates'}. A quote will differ.
            </p>

            {/*
              * THE TABLE BELONGS TO THE SHAPE THAT IS SELECTED, and it did not (owner-caught,
              * 27 Sep 2026). With the guarantee chosen it still drew the capital access schedule —
              * the lump sum falling to nothing by the end of the period — directly beneath a card
              * reading "$100,000 always". That is the exact thing the life policy exists to prevent,
              * so the screen was contradicting itself on the single point that distinguishes this
              * option from the one above it.
              *
              * The guaranteed shape has no taper on the death benefit at all: the annuity's own
              * benefit still runs down, and the policy pays the difference, so the family receives
              * the whole amount whenever death comes. What we CANNOT draw is its cash-out line: that
              * depends on how the provider splits the premium between the annuity and the policy,
              * and on a surrender value struck from their own mortality basis. Only the quote has
              * it, so we say so rather than reuse a curve that does not apply.
              */}
            {selected === 'guaranteed' ? (
                <div className="rounded-lg border border-slate-200 border-l-4 border-l-violet-500 bg-white px-3 py-2">
                    <p className="text-[11.5px] font-semibold text-slate-800 mb-1">What the guarantee changes</p>
                    <p className="text-[11px] leading-relaxed text-slate-600">
                        Nothing tapers. Your family receives the full <strong>{formatCurrency(purchaseAmount)}</strong> whenever
                        death comes — in year two or in year thirty. That is what the life policy is for, and it is the whole
                        difference between this option and the one above, where the lump sum falls to nothing
                        {periodYears ? ` after ${periodYears} years` : ' by the end of the period'}.
                    </p>
                    <p className="mt-1.5 text-[11px] leading-relaxed text-slate-600">
                        The cash-out value does still fall. We cannot put a figure on it the way we can for the other two,
                        because it depends on how the provider splits your money between the annuity and the policy. Their
                        quote states it.
                    </p>
                </div>
            ) : schedule.length > 0 && (
                <details className="rounded-lg border border-slate-200 border-l-4 border-l-slate-300 bg-white px-3 py-2">
                    <summary className="text-[11.5px] font-medium text-blue-700 cursor-pointer list-none">See what those two figures do year by year</summary>
                    <div className="mt-2 grid grid-cols-3 gap-x-3 gap-y-1 text-[11.5px] text-slate-700">
                        <span className="text-[9.5px] uppercase tracking-wide font-bold text-slate-500">Year</span>
                        <span className="text-[9.5px] uppercase tracking-wide font-bold text-slate-500 text-right">{deathLabel}</span>
                        <span className="text-[9.5px] uppercase tracking-wide font-bold text-slate-500 text-right">If cashed out</span>
                        {schedule.map(row => (
                            <React.Fragment key={row.yearsElapsed}>
                                <span>{row.yearsElapsed === 0 ? 'Now' : row.yearsElapsed === periodYears ? `${scheduleYearLabel(row.yearsElapsed)} onward` : scheduleYearLabel(row.yearsElapsed)}</span>
                                <span className={`text-right font-bold ${row.onDeath > 0 ? 'text-slate-900' : 'text-rose-700'}`}>
                                    {row.onDeath > 0 ? formatCurrency(row.onDeath) : 'nothing'}
                                </span>
                                <span className={`text-right font-bold ${row.ifCashedOut > 0 ? 'text-slate-900' : 'text-rose-700'}`}>
                                    {row.ifCashedOut > 0 ? formatCurrency(row.ifCashedOut) : 'nothing'}
                                </span>
                            </React.Fragment>
                        ))}
                    </div>
                    <p className="mt-1.5 text-[11px] leading-relaxed text-slate-600">
                        {withdrawalWords({ residualPct: residualPercent, periodYears, ownerName })}
                    </p>
                </details>
            )}
        </div>
    );
};

export default AnnuityShapeChoice;
